/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { Button } from "semantic-ui-react";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { calculateBmi } from "@teamprevent/common/bmi/calculateBmi";
import { HealthSurveillanceQuestionConfig } from "@teamprevent/common/healthSurveillance";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";

import * as questionIds from "../consultationQuestions/questionIds";
import { bmiQuestion, diastolicFiveQuestion, diastolicFourQuestion, diastolicThreeQuestion, diastolicTwoQuestion, systolicFiveQuestion,
    systolicFourQuestion, systolicThreeQuestion, systolicTwoQuestion } from "../consultationQuestions/introQuestions";

import "../styles/health-surveillance-consultation-health-questions-section.scss";
import { FailOption, PassOption, PulseRhythmIrregular, PulseRhythmRegular } from "../models";
import { sections } from "../consultation";

// eslint-disable-next-line max-len
const HealthSurveillanceConsultationHealthQuestionsSectionUnconnected: React.FunctionComponent<CustomQuestionsQuestionnaireSectionProps> = (props: CustomQuestionsQuestionnaireSectionProps) => {
    const [additionalBloodPressureReadings, setAdditionalBloodPressureReadings] = React.useState(0);

    const updateBmiQuestion = (heightInCms: number, weightInKgs: number) => {
        props.updateAnswer({
            sectionId: sections.HealthQuestions.id,
            id: bmiQuestion.id,
            questionnaireKey: props.questionnaireKey,
            question: {
                question: bmiQuestion.questionText,
                isValid: true,
                answer: weightInKgs && heightInCms && calculateBmi({ weightInKgs, heightInCms })
            }
        });
    };

    const onHeightChanged = (heightInCms: number) => {
        const weightInKgs = props.questions[questionIds.Weight].answer as number;
        updateBmiQuestion(heightInCms, weightInKgs);
    };
    const onWeightChanged = (weightInKgs: number) => {
        const heightInCms = props.questions[questionIds.Height].answer as number;
        updateBmiQuestion(heightInCms, weightInKgs);
    };

    const addBloodPressureReading = () => {
        if (additionalBloodPressureReadings < 4) {
            setAdditionalBloodPressureReadings(additionalBloodPressureReadings + 1);
        }
    };
    const removeBloodPressureReading = () => {
        if (additionalBloodPressureReadings > 0) {
            let bloodPressureQuestions: HealthSurveillanceQuestionConfig[] = [];
            switch (additionalBloodPressureReadings) {
                case 1:
                    bloodPressureQuestions = [systolicTwoQuestion, diastolicTwoQuestion];
                    break;
                case 2:
                    bloodPressureQuestions = [systolicThreeQuestion, diastolicThreeQuestion];
                    break;
                case 3:
                    bloodPressureQuestions = [systolicFourQuestion, diastolicFourQuestion];
                    break;
                case 4:
                    bloodPressureQuestions = [systolicFiveQuestion, diastolicFiveQuestion];
                    break;
                default:
                    break;
            }

            bloodPressureQuestions.forEach(bloodPressureQuestion => props.updateAnswer({
                sectionId: sections.HealthQuestions.id,
                id: bloodPressureQuestion.id,
                questionnaireKey: props.questionnaireKey,
                question: {
                    question: bloodPressureQuestion.questionText,
                    isValid: true,
                    answer: undefined
                }
            }));

            setAdditionalBloodPressureReadings(additionalBloodPressureReadings - 1);
        }
    };

    React.useEffect(() => {
        if (additionalBloodPressureReadings === 0 && (
            props.questions[questionIds.SystolicBloodPressureTwo]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureTwo]?.answer ||
            props.questions[questionIds.SystolicBloodPressureThree]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureThree]?.answer ||
            props.questions[questionIds.SystolicBloodPressureFour]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureFour]?.answer ||
            props.questions[questionIds.SystolicBloodPressureFive]?.answer ||
            props.questions[questionIds.DiastolicBloodPressureFive]?.answer)) {
            if (props.questions[questionIds.SystolicBloodPressureTwo]?.answer || props.questions[questionIds.DiastolicBloodPressureTwo]?.answer) {
                setAdditionalBloodPressureReadings(1);
            }
            if (props.questions[questionIds.SystolicBloodPressureThree]?.answer || props.questions[questionIds.DiastolicBloodPressureThree]?.answer) {
                setAdditionalBloodPressureReadings(2);
            }
            if (props.questions[questionIds.SystolicBloodPressureFour]?.answer || props.questions[questionIds.DiastolicBloodPressureFour]?.answer) {
                setAdditionalBloodPressureReadings(3);
            }
            if (props.questions[questionIds.SystolicBloodPressureFive]?.answer || props.questions[questionIds.DiastolicBloodPressureFive]?.answer) {
                setAdditionalBloodPressureReadings(4);
            }
        }
    }, [props.questions]);

    const questions = Object.values(props.questions || {});
    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.HealthQuestions}
        >
            {YesNoWithFollowUpQuestion({
                readonly: props.readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.HealthUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.HealthUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.HealthUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OnReviewOfQuestionnaireByEmployeeHaveTheyTickedYesToAny)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.HealthQuestions.id
                    }}
                />
                {/* eslint-disable-next-line max-len */}
                {getAnswerToQuestionWithId(questions, questionIds.OnReviewOfQuestionnaireByEmployeeHaveTheyTickedYesToAny) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PleaseProvideFurtherInformationOnTheConditionSymptomsHighlighted)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId: sections.HealthQuestions.id
                        }}
                    />
                </>}
                <NumberQuestion
                    min={130}
                    max={220}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Height)}
                    mandatory
                    onAnswerChange={onHeightChanged}
                />
                <NumberQuestion
                    min={25}
                    max={635}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Weight)}
                    mandatory
                    onAnswerChange={onWeightChanged}
                />
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Bmi)}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressure)}
                    mandatory
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressure)}
                    mandatory
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureTwo)}
                    mandatory={additionalBloodPressureReadings >= 1}
                    hidden={additionalBloodPressureReadings < 1}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureTwo)}
                    mandatory={additionalBloodPressureReadings >= 1}
                    hidden={additionalBloodPressureReadings < 1}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureThree)}
                    mandatory={additionalBloodPressureReadings >= 2}
                    hidden={additionalBloodPressureReadings < 2}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureThree)}
                    mandatory={additionalBloodPressureReadings >= 2}
                    hidden={additionalBloodPressureReadings < 2}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureFour)}
                    mandatory={additionalBloodPressureReadings >= 3}
                    hidden={additionalBloodPressureReadings < 3}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureFour)}
                    mandatory={additionalBloodPressureReadings >= 3}
                    hidden={additionalBloodPressureReadings < 3}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SystolicBloodPressureFive)}
                    mandatory={additionalBloodPressureReadings >= 4}
                    hidden={additionalBloodPressureReadings < 4}
                />
                <NumberQuestion
                    min={30}
                    max={250}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DiastolicBloodPressureFive)}
                    mandatory={additionalBloodPressureReadings >= 4}
                    hidden={additionalBloodPressureReadings < 4}
                />
                {!props.readonly && additionalBloodPressureReadings < 4 ? (
                    <Button onClick={addBloodPressureReading} content="Add Another Blood Pressure Reading" secondary />
                ) : ("")}
                {!props.readonly && additionalBloodPressureReadings > 0 ? (
                    <Button onClick={removeBloodPressureReading} content="Remove Last Blood Pressure Reading" secondary />
                ) : ("")}
                {!props.readonly ? (<div className={"fields-divider"} />) : ("")}
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseRate)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseRhythm)}
                    values={[PulseRhythmRegular, PulseRhythmIrregular]}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.HealthQuestions.id
                    }}
                />
                <NumberQuestion
                    min={0.5}
                    max={6}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Cholesterol)}
                />
                {(!props.readonly || checkExistenceOfQuestionWithId(questions, questionIds.BloodSugar)) &&
                    <NumberQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodSugar)}
                    />
                }
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Psa)}
                    mandatory
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GPSelfReferralLetterIssuedRegardingBloodPressureOrPulse)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.HealthQuestions.id
                    }}
                />
                {(!props.readonly || checkExistenceOfQuestionWithId(questions, questionIds.Whisper)) && <>
                    <MultiButtonQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Whisper)}
                        mandatory
                    />
                    {getAnswerToQuestionWithId(questions, questionIds.Whisper) === MultiButtonQuestion.YesOption && <>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhisperLeftEarPassed)}
                            values={[PassOption, FailOption]}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WhisperRightEarPassed)}
                            values={[PassOption, FailOption]}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodLeftEar)}
                            values={[PassOption, FailOption]}
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId: sections.HealthQuestions.id
                            }}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.BloodRightEar)}
                            values={[PassOption, FailOption]}
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId: sections.HealthQuestions.id
                            }}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.GPSelefReferralLetterIssuedRegardingWhisperTestResult)}
                            mandatory
                            outdated={{
                                isOutdated: false,
                                questionnaireKey: props.questionnaireKey,
                                sectionId: sections.HealthQuestions.id
                            }}
                        />
                    </>}
                </>}
            </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const HealthSurveillanceConsultationHealthQuestionsSection = connect(makeMapStateToQuestionnaireSectionProps(sections.HealthQuestions.id), mapDispatchToQuestionnaireSectionProps)(HealthSurveillanceConsultationHealthQuestionsSectionUnconnected);
